/**
 * Normalizes API draft response shape (daySuggestions with meals array)
 * to the UI DraftData shape (suggestions with meals Record<string, DraftMealData>).
 */

import type {
  DraftData,
  DraftSuggestion,
  DraftMealData,
  DraftRecipe,
  DraftSearchParams,
  GenerateDraftApiResponse,
  GenerateDraftApiDaySuggestion,
  GenerateDraftApiDayMeal,
} from '@/types/meal-plan';
import { resolveMealDisplayKey } from '@/utils/meal-type-utils';

function mapRecipeSuggestionToDraftRecipe(
  recipe: GenerateDraftApiDayMeal['recipeSuggestions'][number],
  selectedRecipeId: string | null
): DraftRecipe {
  const source = (recipe.source === 'edamam' ? 'edamam' : 'spoonacular') as 'edamam' | 'spoonacular';
  return {
    id: recipe.id,
    title: recipe.title,
    image: recipe.image ?? '',
    readyInMinutes: recipe.readyInMinutes,
    servings: recipe.servings,
    nutritionServings: recipe.nutritionServings,
    source,
    sourceUrl: '',
    fromCache: false,
    calories: recipe.calories,
    protein: recipe.protein,
    carbs: recipe.carbs,
    fat: recipe.fat,
    fiber: recipe.fiber ?? 0,
    isSelected: recipe.id === selectedRecipeId,
    selectedAt: recipe.id === selectedRecipeId ? new Date().toISOString() : null,
    customizations: {},
  };
}

function normalizeDaySuggestion(day: GenerateDraftApiDaySuggestion): DraftSuggestion {
  const meals: Record<string, DraftMealData> = {};
  for (const [mealIndex, meal] of day.meals.entries()) {
    const baseKey = resolveMealDisplayKey(
      meal.mealName,
      meal.mealType,
      `meal_${meal.mealOrder ?? mealIndex + 1}`
    );
    let key = baseKey;
    let duplicateIndex = 2;
    while (meals[key]) {
      key = `${baseKey}_${duplicateIndex}`;
      duplicateIndex += 1;
    }

    const recipes: DraftRecipe[] = (meal.recipeSuggestions ?? []).map((r) =>
      mapRecipeSuggestionToDraftRecipe(r, meal.selectedRecipeId ?? null)
    );
    meals[key] = {
      recipes,
      customizations: {},
      totalNutrition: null,
      selectedRecipeId: meal.selectedRecipeId ?? null,
      mealOrder:
        typeof meal.mealOrder === 'number' && meal.mealOrder > 0
          ? meal.mealOrder
          : mealIndex + 1,
      pagination: undefined,
    };
  }
  return {
    day: day.dayNumber,
    date: day.date,
    meals,
  };
}

/**
 * Build minimal DraftSearchParams from API response for normalizer output.
 */
function buildSearchParams(api: GenerateDraftApiResponse): DraftSearchParams {
  return {
    days: api.days,
    startDate: api.startDate,
    aiProvider: '',
    clientGoals: {
      calories: api.targetCalories ?? 0,
      protein: 0,
      proteinMin: 0,
      proteinMax: 0,
      carbs: 0,
      carbsMin: 0,
      carbsMax: 0,
      fat: 0,
      fatMin: 0,
      fatMax: 0,
      fiber: 0,
    },
    mealProgram: undefined,
    dietaryPreferences: {
      allergies: [],
      cuisineTypes: [],
      dietaryPreferences: [],
    },
    overrideClientGoals: null,
    overrideMealProgram: undefined,
  };
}

/**
 * Normalize API draft response (daySuggestions, meals array) to DraftData (suggestions, meals Record).
 * Handles both raw spec shape and wrapped { success, data } when data has daySuggestions.
 */
export function normalizeDraftApiResponseToDraftData(
  response: GenerateDraftApiResponse | { success?: boolean; data?: GenerateDraftApiResponse }
): DraftData {
  const api: GenerateDraftApiResponse =
    'daySuggestions' in response
      ? (response as GenerateDraftApiResponse)
      : (response as { data?: GenerateDraftApiResponse }).data ?? (response as GenerateDraftApiResponse);

  const draftId = api.draftId ?? (api as any).id ?? '';
  const suggestions: DraftSuggestion[] = (api.daySuggestions ?? []).map(normalizeDaySuggestion);

  const apiStatus = (api as { status?: string }).status;
  return {
    id: draftId,
    clientId: api.clientId,
    nutritionistId: '',
    status: apiStatus === 'finalized' ? 'finalized' : 'draft',
    searchParams: buildSearchParams(api),
    suggestions,
    createdAt: api.generatedAt ?? new Date().toISOString(),
    updatedAt: api.generatedAt ?? new Date().toISOString(),
    expiresAt: api.expiresAt ?? null,
  };
}

/**
 * Check if a value looks like the API shape (has daySuggestions with meals array).
 */
export function isApiDraftShape(value: any): value is GenerateDraftApiResponse {
  if (!value || typeof value !== 'object') return false;
  const daySuggestions = value.daySuggestions ?? value.data?.daySuggestions;
  if (!Array.isArray(daySuggestions) || daySuggestions.length === 0) return false;
  const first = daySuggestions[0];
  return Array.isArray(first?.meals) && first.meals.length >= 0;
}

// -----------------------------------------------------------------------------
// Manual meal plan draft normalizer
// -----------------------------------------------------------------------------

/** Manual draft API meal - has recipes[] and ingredients[] (not recipeSuggestions) */
interface ManualDraftApiMeal {
  mealOrder?: number;
  mealName?: string;
  mealType?: string;
  mealTime?: string;
  targetCalories?: number;
  notes?: string | null;
  recipes?: Array<{
    id?: string;
    title?: string;
    recipeName?: string;
    image?: string;
    calories?: number;
    protein?: number;
    carbs?: number;
    fat?: number;
    source?: string;
    recipeSource?: string;
    nutritionServings?: number;
    servings?: number;
    defaultServingId?: string | null;
    defaultServingGrams?: number | null;
    selectedServingId?: string | null;
    selectedServingGrams?: number | null;
    servingSizeMultiplier?: number;
    servingSizes?: Array<{
      servingId: string;
      grams: number;
      label?: string | null;
      isDefault?: boolean;
    }>;
    healthLabels?: string[];
    nutrition?: Record<string, unknown>;
    [key: string]: unknown;
  }>;
  ingredients?: Array<{
    id?: string;
    name?: string;
    amount?: number;
    unit?: string;
    calories?: number;
    protein?: number;
    carbs?: number;
    fat?: number;
    [key: string]: unknown;
  }>;
  totalNutrition?: Record<string, unknown>;
  [key: string]: unknown;
}

/** Manual draft API day suggestion */
interface ManualDraftApiDaySuggestion {
  dayNumber?: number;
  date?: string;
  dayName?: string | null;
  notes?: string | null;
  meals?: ManualDraftApiMeal[];
  [key: string]: unknown;
}

/** Manual draft API response shape */
interface ManualDraftApiResponse {
  id?: string;
  clientId?: string;
  clientName?: string;
  days?: number;
  durationDays?: number;
  startDate?: string;
  endDate?: string;
  creationMethod?: string;
  status?: string;
  generatedAt?: string;
  expiresAt?: string | null;
  daySuggestions?: ManualDraftApiDaySuggestion[];
  dailyNutrition?: unknown[];
  averageDailyNutrition?: unknown;
  [key: string]: unknown;
}

/**
 * Normalize manual meal plan draft API response to the shape expected by ManualMealPlanDraftPage.
 * Transforms daySuggestions (meals array) → suggestions (meals Record keyed by mealName).
 */
export function normalizeManualDraftApiResponse(
  response: ManualDraftApiResponse | { success?: boolean; data?: ManualDraftApiResponse }
): ManualDraftApiResponse {
  const api: ManualDraftApiResponse =
    typeof (response as { data?: ManualDraftApiResponse }).data !== 'undefined'
      ? (response as { data?: ManualDraftApiResponse }).data!
      : (response as ManualDraftApiResponse);

  const daySuggestions = api.daySuggestions ?? [];
  const suggestions = daySuggestions.map((ds: ManualDraftApiDaySuggestion) => {
    const mealsArray = ds.meals ?? [];
    const meals: Record<string, ManualDraftApiMeal> = {};
    for (const [mealIndex, meal] of mealsArray.entries()) {
      const mealAny = meal as Record<string, unknown>;
      const apiStableId = String(mealAny.id ?? mealAny.mealId ?? '').trim();
      const baseKey = resolveMealDisplayKey(
        meal.mealName,
        meal.mealType,
        apiStableId || `meal_${meal.mealOrder ?? mealIndex + 1}`
      );
      let key = baseKey;
      let duplicateIndex = 2;
      while (meals[key]) {
        key = `${baseKey}_${duplicateIndex}`;
        duplicateIndex += 1;
      }
      meals[key] = meal;
    }
    return {
      day: ds.dayNumber ?? 0,
      date: ds.date ?? '',
      dayName: ds.dayName ?? null,
      notes: ds.notes ?? null,
      meals,
    };
  });

  return {
    ...api,
    suggestions,
    durationDays: api.durationDays ?? api.days,
  };
}

/** Check if value is manual draft API shape (has daySuggestions with meals having recipes/ingredients, not recipeSuggestions) */
export function isManualDraftApiShape(value: any): boolean {
  if (!value || typeof value !== 'object') return false;
  const ds = value.daySuggestions ?? value.data?.daySuggestions;
  if (!Array.isArray(ds) || ds.length === 0) return false;
  const first = ds[0];
  const meals = first?.meals;
  if (!Array.isArray(meals) || meals.length === 0) return true; // Empty manual draft
  const firstMeal = meals[0];
  return 'recipes' in firstMeal || 'ingredients' in firstMeal;
}
